import { cn } from "@/utils";
import { ChevronRight } from "lucide-react";
import React from "react";

export interface WorkspaceBrowserCrumb {
  key: string;
  label: string;
  /** Absent on the current (last) crumb, which isn't a link. */
  onNavigate?: () => void;
  /** Wraps the crumb in a drop target so items can be dragged up the tree. */
  wrap?: (children: React.ReactNode) => React.ReactNode;
}

/**
 * Folder trail for a typed workspace-browser tab, rendered between the tab
 * strip and the list while inside a folder. The root and ancestor crumbs
 * navigate (and act as drop targets); the current folder is inert text.
 */
export function WorkspaceBrowserBreadcrumbs({
  crumbs,
}: {
  crumbs: WorkspaceBrowserCrumb[];
}) {
  if (crumbs.length < 2) return null;

  return (
    <nav
      aria-label="Folder trail"
      className="flex min-w-0 items-center gap-1 px-1 text-sm"
    >
      {crumbs.map((crumb, i) => {
        const isLast = i === crumbs.length - 1;
        const node = crumb.onNavigate ? (
          <button
            type="button"
            onClick={crumb.onNavigate}
            className="max-w-48 truncate rounded-sm px-1 py-0.5 text-muted-foreground outline-none hover:bg-n-05 hover:text-foreground focus-visible:outline-2 focus-visible:outline-rust"
          >
            {crumb.label}
          </button>
        ) : (
          <span
            className={cn(
              "max-w-48 truncate px-1 py-0.5",
              isLast ? "font-medium text-foreground" : "text-muted-foreground",
            )}
          >
            {crumb.label}
          </span>
        );
        return (
          <React.Fragment key={crumb.key}>
            {i > 0 && (
              <ChevronRight className="size-3.5 shrink-0 text-muted-foreground/60" />
            )}
            {crumb.wrap ? crumb.wrap(node) : node}
          </React.Fragment>
        );
      })}
    </nav>
  );
}
